import { useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { useCognitoGroups } from './useCognitoGroups';

import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { getImpersonationTargetsV2QueryOptions } from '@/services/getImpersonationTargetsV2';
import {
  actAsUserIdAtom,
  authenticatedUserIdAtom,
  store,
  userIdAtom,
} from '@/services/globalState';
import { clearImpersonation, saveImpersonation } from '@/util/impersonationStorage';
import { queryClient } from '@/util/queryClient';

/**
 * Provides account impersonation for Primum team members.
 *
 * Allows switching between the authenticated user's own identity and the
 * accounts the API says they may act as: the support accounts for `primum`
 * and `operations`, and any account for `dev` (`scope: 'all'`), found by
 * search. The API enforces the same rule on every `X-Act-As` request; this
 * hook only decides what to offer. Switching pre-fetches the new
 * identity's user data so the secure layout never sees a loading state
 * or stale NPI verification flags. Nothing else is invalidated: every query
 * whose data depends on the acting identity carries the effective user ID in
 * its key, so the new identity misses the old one's entries on its own and a
 * switch back finds its own cache intact. A new per-user query must do the same.
 */
export function useAccountSwitcher() {
  const { isDevUser, isPrimumUser, isOperationsUser, isLoading } = useCognitoGroups();

  const { data: targets } = useQuery(
    getImpersonationTargetsV2QueryOptions(isDevUser || isPrimumUser || isOperationsUser)
  );

  const scope = targets?.scope ?? 'none';

  const supportAccounts = useMemo(
    () =>
      (targets?.supportAccounts ?? []).map((a) => ({
        id: String(a.id),
        label: a.label,
        description: a.description,
      })),
    [targets]
  );

  const [actAsUserId, setActAsUserId] = useAtom(actAsUserIdAtom);

  const effectiveUserId = useAtomValue(userIdAtom);

  const { data: actingAsUser } = useQuery({
    ...getCurrentUserV2QueryOptions(actAsUserId),
    enabled: !!actAsUserId,
  });

  const currentAccount = useMemo(() => {
    if (!actAsUserId) {
      return undefined;
    }

    const supportAccount = supportAccounts.find((account) => account.id === actAsUserId);

    if (supportAccount) {
      return supportAccount;
    }

    const name = [actingAsUser?.firstName, actingAsUser?.lastName].filter(Boolean).join(' ');

    return { id: actAsUserId, label: name || `Account ${actAsUserId}`, description: '' };
  }, [actAsUserId, actingAsUser, supportAccounts]);

  const switchToAccount = async (userId: string | undefined) => {
    if (userId !== undefined && scope !== 'all' && !supportAccounts.some((a) => a.id === userId)) {
      return;
    }

    // Update the identity atom first so the fetchClient middleware sends the
    // correct X-Act-As header for the prefetch request.
    setActAsUserId(userId);

    const authenticatedUserId = store.get(authenticatedUserIdAtom);

    if (userId && authenticatedUserId) {
      await saveImpersonation(authenticatedUserId, userId);
    } else {
      await clearImpersonation();
    }

    // Pre-fetch and cache the new identity's user data before invalidating
    // queries. This ensures the secure layout has real user data (including
    // validNpi) when it re-renders, preventing loading flashes or spurious
    // NPI verification redirects.
    if (userId) {
      await queryClient.fetchQuery(getCurrentUserV2QueryOptions(userId));
    }
  };

  const switchToSelf = async () => {
    setActAsUserId(undefined);

    await clearImpersonation();
  };

  return {
    /** Whether the account switcher feature is enabled for the current user */
    isEnabled: scope !== 'none',
    /** Whether the user may search for and act as any account, not only support accounts */
    canSearch: scope === 'all',
    /** Whether the hook is still loading user data */
    isLoading,
    /** The user ID of the account currently being acted as (undefined = self) */
    actAsUserId,
    /**
     * The active user ID — impersonated account if set, otherwise the authenticated user.
     *
     * @deprecated For new code, prefer reading `userIdAtom` directly instead of using this property.
     * This property exists for backward compatibility with existing test names.
     */
    effectiveUserId,
    /**
     * The account currently being acted as: the support account entry, or for a
     * searched account its name from the acting user's profile.
     */
    currentAccount,
    /** List of available support accounts */
    supportAccounts,
    /** Switch to act as a support account, or with `canSearch` any account */
    switchToAccount,
    /** Switch back to acting as yourself */
    switchToSelf,
    /** Whether currently acting as another account */
    isActingAsOther: !!actAsUserId,
  };
}
